import React from "react";
import styles from "./QuoatesCard.module.scss";
import {
  ZitateGridOptionsType,
  ZitateSliderOptionsType,
} from "@/types/ModuleOptionsType";
import { customSanitize } from "../../utils/customSanitize";
import Image from "next/image";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { UserImage } from "./UserImage/UserImage";
import { QuotesElementType } from "@/modules/QuoteSliderModule/QuoteSliderModule";
import { Button } from "../Button/Button";
import { CustomSVG } from "../_primitives/CustomSVG/CustomSVG";

export type QuoatesCardOptions = ZitateSliderOptionsType &
  ZitateGridOptionsType;

interface QuoatesCardProps {
  quotesOptions: QuoatesCardOptions;
  element: QuotesElementType;
  showAvatar?: boolean;
  showRating?: boolean;
  cardIndex: number;
  moduleIndex: number;
  cardType: "slider" | "grid";
  headling_level: number;
  skeleton?: boolean;
}

export const QuoatesCard = ({
  element,
  quotesOptions,
  showAvatar = false,
  showRating = false,
  cardIndex,
  moduleIndex,
  cardType,
  headling_level,
  skeleton = false,
}: QuoatesCardProps) => {
  const { getLabel, getIcon } = useGlobalContext();
  const {
    // max_character,
    box: { show_border_radius, show_box_shadow, show_card_box },
  } = quotesOptions;

  const skeletonElementClass = skeleton ? "skeleton-element" : "";

  const {
    title,
    content,
    firma,
    rating_stars: ratingStars,
    name,
    firstname,
    position,
    source,
    image,
  } = element;

  const textClass = element.content_size === "small" ? "" : "quotes-slider-big";
  const hasSource =
    (source?.type === "logo" && source.logo) ||
    (source?.type === "text" && source.text);

  const QuotesJSX = (
    <>
      <div className="is-flex">
        <span className={`quotes-name is-block ${skeletonElementClass}`}>
          {firstname && `${firstname} `} {name && `${name}, `}
        </span>
        <span
          className={`pl-1 quotes-source-position is-block ${skeletonElementClass}`}
        >
          {position}
          {position && firma && ", "}
          {firma}
        </span>
      </div>

      {element.link && (
        <Button
          // className="mt-auto"
          type="primary"
          href={element.link.url}
          target="_self"
          aria-label={element.link.aria_label || ""}
          className={`${skeletonElementClass}`}
        >
          {element.link.title || getLabel("read_more_label")}
        </Button>
      )}
    </>
  );

  const finalAvatarImage = image;
  const hasAvatar = !!finalAvatarImage && showAvatar;
  return (
    <article
      data-card-type={cardType}
      className={`${styles["quote-card"]} ${cardType === "slider" ? (!hasAvatar ? styles["no-image"] : "") : styles["no-image"]} quote-card ${ratingStars && showRating ? `${styles["has-rating"]}` : ""} ${hasSource ? styles["has-source"] : ""}`}
    >
      <div className="columns is-mobile is-centered is-multiline">
        <header className="column is-12-mobile is-12-tablet is-4-desktop is-3-widescreen">
          {cardType === "grid" && (
            <div className="is-flex is-justify-content-space-between is-align-items-center">
              {ratingStars && showRating && (
                <Stars
                  rating={ratingStars}
                  cardIndex={cardIndex}
                  moduleIndex={moduleIndex}
                  extraClass="shrink-0"
                />
              )}

              <SourceDisplay
                source={source}
                extraClass={`shrink-0 ${skeletonElementClass}`}
              />
            </div>
          )}
          {hasAvatar && cardType === "slider" && (
            <UserImage data-card-type={cardType} image={finalAvatarImage} />
          )}
          {cardType === "slider" && (
            <>
              {ratingStars && showRating && (
                <Stars
                  rating={ratingStars}
                  cardIndex={cardIndex}
                  moduleIndex={moduleIndex}
                />
              )}
              <SourceDisplay source={source} />
            </>
          )}
          {hasAvatar && cardType === "slider" && (
            <div aria-hidden={true} className={styles["quote-icon"]}>
              <CustomSVG
                src={getIcon("quote")}
                wrapper={"span"}
                width={40}
                height={24}
                alt="play"
                className="is-flex"
              />
            </div>
          )}
        </header>

        <div
          className={` column is-12-mobile is-12-tablet is-8-desktop is-9-widescreen ${styles["quote-wrapper"]}  `}
        >
          <div
            className={`${show_card_box ? "is-box h-100" : ""} ${show_border_radius ? styles["has-border-radius"] : ""} ${show_box_shadow ? styles["has-box-shadow"] : ""}`}
          >
            <section className={styles["text-wrapper"]}>
              <p role="heading" aria-level={headling_level} className={"title"}>
                {title}
              </p>
              <blockquote
                className={`p ${textClass ? textClass : ""} ${skeletonElementClass}`}
                dangerouslySetInnerHTML={{ __html: customSanitize(content) }}
              />
            </section>

            <footer className={styles["user-info"]}>
              {finalAvatarImage && showAvatar && cardType === "grid" && (
                <UserImage data-card-type={cardType} image={finalAvatarImage} />
              )}

              {cardType === "slider" ? QuotesJSX : <div>{QuotesJSX}</div>}
            </footer>
          </div>
        </div>
      </div>
    </article>
  );
};

const SourceLogo: React.FC<{ logo: QuotesElementType["source"]["logo"] }> = ({
  logo,
}) => {
  if (!logo) return null;
  return (
    <div className={styles["source-logo"]}>
      <Image
        src={logo.url}
        alt={logo.alt}
        width={logo.width}
        height={logo.height}
        style={{ objectFit: "cover" }}
        sizes="(max-width: 1407px) 25vw, 375px"
        // blurDataURL={`data:${logo.mime_type}${logo.image_blur}`}
        // placeholder="blur"
      />
    </div>
  );
};

const SourceText: React.FC<{
  text: QuotesElementType["source"]["text"];
}> = ({ text }) => <div className={"quotes-source-position"}>{text}</div>;

const SourceDisplay: React.FC<{
  source: QuotesElementType["source"];
  extraClass?: string;
}> = ({ source, extraClass = "" }) => {
  if (
    (source.type === "logo" && !source.logo) ||
    (source.type === "text" && !source.text)
  )
    return null;
  return (
    <div className={`${styles.source} ${extraClass}`}>
      {source.type === "logo" && source.logo && (
        <SourceLogo logo={source.logo} />
      )}
      {source.type === "text" && source.text && (
        <SourceText text={source.text} />
      )}
    </div>
  );
};
const StarIcon = ({
  fillPercentage,
  starIndex,
  color = "currentColor",
  cardIndex,
  moduleIndex,
}: {
  fillPercentage: number;
  starIndex: number;
  color?: string;
  cardIndex: number;
  moduleIndex: number;
}) => {
  const gradientId = `partial-fill-${moduleIndex}-${cardIndex}-${starIndex}`;

  return (
    <svg
      viewBox="0 0 24 24"
      className={styles.star}
      aria-hidden="true"
      width="24"
      height="24"
    >
      <defs>
        <linearGradient id={gradientId}>
          <stop offset={`${fillPercentage}%`} stopColor={color} />
          <stop offset={`${fillPercentage}%`} stopColor="transparent" />
        </linearGradient>
      </defs>
      <path
        d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"
        fill={`url(#${gradientId})`}
      />
      <path
        d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"
        fill="none"
        stroke={color}
      />
    </svg>
  );
};

interface StarsProps {
  rating: number;
  moduleIndex: number;
  cardIndex: number;
  extraClass?: string;
}

const Stars = ({
  rating,
  moduleIndex,
  cardIndex,
  extraClass = "",
}: StarsProps) => {
  const fullStars = Math.floor(rating);
  const partialStarPercentage = (rating - fullStars) * 100;
  const emptyStars = 5 - Math.ceil(rating);
  const { getLabel } = useGlobalContext();
  const label = getLabel("rating_label").replace("$rating", rating.toString());

  return (
    <span
      role="img"
      aria-label={label}
      className={`${styles.rating} ${extraClass}`}
    >
      <span className={`is-flex`} aria-hidden="true">
        {Array.from({ length: fullStars }, (_, i) => (
          <StarIcon
            key={`full-${i}`}
            fillPercentage={100}
            starIndex={i}
            color={"var(--quotes-slider-stars-color)"}
            cardIndex={cardIndex}
            moduleIndex={moduleIndex}
          />
        ))}
        {partialStarPercentage > 0 && (
          <StarIcon
            fillPercentage={partialStarPercentage}
            starIndex={fullStars}
            color={"var(--quotes-slider-stars-color)"}
            cardIndex={cardIndex}
            moduleIndex={moduleIndex}
          />
        )}
        {Array.from({ length: emptyStars }, (_, i) => (
          <StarIcon
            key={`empty-${i}`}
            fillPercentage={0}
            starIndex={fullStars + i + 1}
            color={"var(--quotes-slider-stars-color)"}
            cardIndex={cardIndex}
            moduleIndex={moduleIndex}
          />
        ))}
      </span>
    </span>
  );
};
